<script>
  // @ts-check

  import Link from "../atoms/Link.svelte";
  import Image from "../atoms/Image.svelte";
  import { v4 as uuid } from "@lukeed/uuid";
  import { getTheme } from "../utilities/themes";
  import Grid from "../atoms/Grid.svelte";
  import DynamicHeading from "../atoms/DynamicHeading.svelte";
  import DynamicHeadingContext from "../atoms/DynamicHeadingContext.svelte";

  /** @type { string } */
  let className = "";
  export { className as class };

  /** Primarily used for testing
   * @type {string} */
  export let id = undefined;

  /**
   * The featured image in the card
   * @type {import('../types/shared.js').Image} Image
   */
  export let featuredImage = undefined;

  /**
   * The title of the component
   * @type {string}
   */
  export let title;

  /**
   * The optional description body of the component
   * @type {string}
   */
  export let description = null;

  /**
   * The call to action for the component
   * @type {import('../types/shared.js').CTA} CTA
   */
  export let cta = null;

  /**
   * The theme of the hero section
   * @type {import('../utilities/themes.js').ThemeName} ThemeName
   */
  export let theme = "lightBlue";
  let currentTheme = getTheme(theme ?? "lightBlue");

  let isCardFocused = false;
  let isLinkHovered = false;

  let linkId = uuid();

  $: focusClass = isCardFocused
    ? "outline-2 outline-dashed outline-offset-[10px] outline-black"
    : "";
  $: imageScaleClass = isLinkHovered ? "scale-[1.10]" : "";
</script>

<DynamicHeadingContext>
  <div
    {id}
    class="{theme !== 'white'
      ? currentTheme.backgroundColor
      : 'bg-transparent'} {currentTheme.textColor} {focusClass} relative p-2 md:p-3 xl:p-4 {className}"
  >
    <Grid>
      <Image
        {...featuredImage}
        aspectRatioClasses="aspect-1/1"
        containerClass="col-full-width overflow-hidden md:col-span-6"
        elementClass="w-full h-auto transition-all duration-[400ms] ease-in-out {imageScaleClass}"
      />
      <div
        class="col-full-width flex flex-col pt-6 md:col-span-6 md:py-5 md:pr-7 lg:py-6 lg:pr-12.5 xl:py-8 xl:pr-15"
      >
        <DynamicHeading class="heading-md">
          {#if cta}
            <a
              id={`cta-${id}`}
              href={cta.url}
              target={cta.url && cta.isExternal ? "_blank" : null}
              rel={cta.url && cta.isExternal ? "noopener noreferrer" : null}
              aria-describedby={linkId}
              class="no-underline outline-none after:absolute after:bottom-0 after:left-0 after:right-0 after:top-0 focus-visible:outline-none"
              on:focusin={() => (isCardFocused = true)}
              on:focusout={() => (isCardFocused = false)}
              on:mouseenter={() => (isLinkHovered = true)}
              on:mouseleave={() => (isLinkHovered = false)}
              data-sveltekit-reload
            >
              {title}
            </a>
          {/if}
        </DynamicHeading>

        {#if description}
          <p class="body-md pt-3 lg:pt-4">
            {description}
          </p>
        {/if}

        {#if cta}
          <Link
            id={linkId}
            class="mt-auto pt-10 lg:pt-15"
            ariaHidden={true}
            label={cta.text}
            variant="large"
            {theme}
            {isLinkHovered}
            href={cta.url}
            isExternal={cta.isExternal}
          />
        {/if}
      </div>
    </Grid>
  </div>
</DynamicHeadingContext>
